/**
 * Debug logger that sends conversation logs to the dev server,
 * which writes them to /logs/dd-mm-yyyy.txt.
 *
 * Enabled when DEBUG=true in .env (injected at build time).
 */

import {ActionOutcome, OUTCOME_MARKERS} from "./actionExecutor";

const IS_DEBUG = process.env.DEBUG === "true";

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function timestamp(): string {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

async function writeLog(entry: string): Promise<void> {
  if (!IS_DEBUG) return;
  try {
    await fetch("/__debug_log", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({entry}),
    });
  } catch {
    // Silently ignore — don't break the add-in if logging fails
  }
}

export function logUserMessage(text: string): void {
  writeLog(
    `\n${"=".repeat(80)}\n` +
      `[${timestamp()}] 💬 USER MESSAGE\n` +
      `${"=".repeat(80)}\n` +
      text +
      "\n",
  );
}

export function logSentToApi(payload: {
  agentId: string;
  messages: Array<{role: string; content: string}>;
  stream: boolean;
}): void {
  const rendered = payload.messages
    .map((m) => `--- ${m.role} ---\n${m.content}`)
    .join("\n\n");

  writeLog(
    `\n${"─".repeat(80)}\n` +
      `[${timestamp()}] 📤 SENT TO LIBRECHAT\n` +
      `${"─".repeat(80)}\n` +
      `Agent ID : ${payload.agentId}\n` +
      `Stream   : ${payload.stream}\n` +
      `\n--- Messages ---\n` +
      rendered +
      "\n",
  );
}

export function logApiResponse(response: string): void {
  writeLog(
    `\n${"─".repeat(80)}\n` +
      `[${timestamp()}] 📥 LIBRECHAT RESPONSE\n` +
      `${"─".repeat(80)}\n` +
      response +
      "\n",
  );
}

export function logActionResults(
  results: Array<{outcome: ActionOutcome; message: string; detail?: string}>,
): void {
  const lines = results
    .map((r) => {
      const line = `  ${OUTCOME_MARKERS[r.outcome]} ${r.message}`;
      return r.detail ? `${line}\n${indent(r.detail)}` : line;
    })
    .join("\n");
  writeLog(
    `\n${"─".repeat(80)}\n` +
      `[${timestamp()}] ⚡ ACTION RESULTS\n` +
      `${"─".repeat(80)}\n` +
      lines +
      "\n",
  );
}

export function logError(context: string, error: unknown): void {
  const msg = error instanceof Error ? error.message : String(error);
  writeLog(
    `\n${"─".repeat(80)}\n` +
      `[${timestamp()}] ❌ ERROR (${context})\n` +
      `${"─".repeat(80)}\n` +
      msg +
      "\n",
  );
}

function indent(text: string): string {
  return text
    .split("\n")
    .map((line) => `      ${line}`)
    .join("\n");
}
